import React, { useEffect, useState } from "react";
import axios from "axios";

function PowerGenerationTabs({ plantId }) {
  const [overview, setOverview] = useState({});
  const [officials, setOfficials] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!plantId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [overviewRes, officialsRes, performanceRes] = await Promise.all([
          axios.get(`https://backend-zkgn.onrender.com/api/plants/${plantId}`),
          axios.get(
            `https://backend-zkgn.onrender.com/api/powergen_officials/plant/${plantId}`
          ),
          axios.get(
            `https://backend-zkgn.onrender.com/api/powergen_performance/${plantId}`
          ),
        ]);

        setOverview(overviewRes.data || {});
        setOfficials(officialsRes.data || []);
        setPerformance(performanceRes.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch plant data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [plantId]);

  if (loading)
    return <div className="text-center my-5">Loading...</div>;
  if (error)
    return <div className="text-danger my-5">{error}</div>;

  return (
    <div className="container my-5">
      {/* Tabs */}
      <ul
        className="nav nav-tabs flex-wrap justify-content-center"
        id="powerTab"
        role="tablist"
      >
        <li className="nav-item">
          <button
            className="nav-link active"
            data-bs-toggle="tab"
            data-bs-target="#overview"
          >
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button
            className="nav-link"
            data-bs-toggle="tab"
            data-bs-target="#officials"
          >
            Officials
          </button>
        </li>
        <li className="nav-item">
          <button
            className="nav-link"
            data-bs-toggle="tab"
            data-bs-target="#performance"
          >
            Performance
          </button>
        </li>
      </ul>

      <div className="tab-content p-3 border border-top-0 rounded-bottom shadow-sm bg-white">
        {/* Overview */}
        <div className="tab-pane fade show active" id="overview">
          <h4 className="mb-3 text-primary">Overview</h4>
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <tbody>
                <tr>
                  <th>Plant Name</th>
                  <td>{overview.genplant_name}</td>
                </tr>
                <tr>
                  <th>Address</th>
                  <td>{overview.genplant_address}</td>
                </tr>
                <tr>
                  <th>City</th>
                  <td>{overview.genplant_city}</td>
                </tr>
                <tr>
                  <th>District</th>
                  <td>{overview.genplant_district}</td>
                </tr>
                <tr>
                  <th>State</th>
                  <td>{overview.genplant_state}</td>
                </tr>
                <tr>
                  <th>Pincode</th>
                  <td>{overview.genplant_pincode}</td>
                </tr>
                <tr>
                  <th>Phone</th>
                  <td>{overview.genplant_phone}</td>
                </tr>
                {overview.genplant_email && (
                  <tr>
                    <th>Email</th>
                    <td>{overview.genplant_email}</td>
                  </tr>
                )}
                {overview.genplant_website && (
                  <tr>
                    <th>Website</th>
                    <td>
                      <a
                        href={
                          overview.genplant_website.startsWith("http")
                            ? overview.genplant_website
                            : `https://${overview.genplant_website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {overview.genplant_website}
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Officials */}
        <div className="tab-pane fade" id="officials">
          <h4 className="mb-3 text-primary">Plant Officials</h4>
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Phone</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {officials.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No officials found
                    </td>
                  </tr>
                ) : (
                  officials.map((o) => (
                    <tr key={o.genofficial_id}>
                      <td>{o.genofficial_name}</td>
                      <td>{o.genofficial_designation}</td>
                      <td>{o.genofficial_phone}</td>
                      <td>{o.genofficial_email}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance */}
        <div className="tab-pane fade" id="performance">
          <h4 className="mb-3 text-primary">Performance</h4>
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Fiscal Year</th>
                  <th>Total Capacity (MW)</th>
                  <th>Hydro (MW)</th>
                  <th>Coal (MW)</th>
                  <th>Gas (MW)</th>
                  <th>Solar (MW)</th>
                  <th>Renewable (MW)</th>
                  <th>Total Generation (MU)</th>
                </tr>
              </thead>
              <tbody>
                {performance.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No performance data available
                    </td>
                  </tr>
                ) : (
                  performance.map((p) => (
                    <tr key={p.gen_perform_id}>
                      <td>{p.fiscal_year}</td>
                      <td>{p.tot_inst_capacity}</td>
                      <td>{p.hydro_mw}</td>
                      <td>{p.coal_mw}</td>
                      <td>{p.gas_mw}</td>
                      <td>{p.solar_mw}</td>
                      <td>{p.renewable_mw}</td>
                      <td>{p.tot_gen_mu}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PowerGenerationTabs;
